import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";

export function DeleteAccountDialog({ open, info, onClose, onDeleted, L }) {
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const byCode = info?.method === "code";
  const sendCode = async () => {
    setBusy(true);
    try { await api.post("/account/delete/send-code"); setSent(true); toast.success(L("تم إرسال الرمز إلى بريدك", "Code sent to your email")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try { await api.post("/account/delete", byCode ? { code: secret } : { password: secret }); toast.success(L("تم حذف حسابك نهائياً", "Your account was permanently deleted")); onDeleted(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="delete-account-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" />{L("حذف الحساب", "Delete account")}</DialogTitle></DialogHeader>
        {info?.blocked ? (
          <div className="flex items-start gap-2 rounded-xl border-2 border-amber-400 bg-amber-50 p-3 text-sm font-bold text-amber-900" data-testid="delete-account-blocked">
            <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" /><span>{info.blocked}</span>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-bold text-red-700 bg-red-50 rounded-xl p-3" data-testid="delete-account-warning">
              {L("سيتم حذف حسابك وجميع بياناتك نهائياً (الإعلانات، المحفظة، الرسائل، المسودات، المفضلة). لا يمكن التراجع عن هذه الخطوة.", "Your account and all your data (ads, wallet, messages, drafts, favorites) will be permanently deleted. This cannot be undone.")}
            </p>
            {byCode && !sent ? (
              <button onClick={sendCode} disabled={busy} className="w-full h-11 rounded-xl bg-[#064E3B] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="delete-account-send-code">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}{L("إرسال رمز التحقق إلى بريدي", "Send verification code to my email")}
              </button>
            ) : (
              <Input type={byCode ? "text" : "password"} inputMode={byCode ? "numeric" : undefined} maxLength={byCode ? 6 : undefined} value={secret} onChange={(e) => setSecret(e.target.value)}
                placeholder={byCode ? L("رمز التحقق (6 أرقام)", "Verification code (6 digits)") : L("أدخل كلمة المرور للتأكيد", "Enter your password to confirm")}
                className="h-11 rounded-xl" dir="ltr" data-testid="delete-account-secret" />
            )}
            {(!byCode || sent) && (
              <button onClick={confirm} disabled={busy || !secret.trim()} className="w-full h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="delete-account-confirm">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}{L("حذف حسابي نهائياً", "Permanently delete my account")}
              </button>
            )}
          </div>
        )}
        <button onClick={onClose} className="w-full h-11 rounded-xl border border-slate-200 font-bold text-slate-700" data-testid="delete-account-cancel">{L("إلغاء", "Cancel")}</button>
      </DialogContent>
    </Dialog>
  );
}
